import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Endpoints } from 'src/app/core/resources/endpoints';
import { environment } from 'src/environments/environment';
import { Utils } from 'src/app/core/resources/utils';
import { Vehicle } from 'src/app/core/interfaces/vehicle';
import { AuthService } from 'src/app/core/services/authentication.service';
import { User } from 'src/app/core/interfaces/user';
import { catchError, finalize, map, shareReplay, timeout } from 'rxjs/operators';
import { PaginationList } from 'src/app/core/models/pagination-list';
import { CargoGpsDTO, Gps } from 'src/app/core/interfaces/gps';
import { StateUserPipe } from 'src/app/core/pipe/stateInactive.pipe';
import { VehicleDocumentCatalog, VehicleDocumentRequired, VehicleDocuments } from 'src/app/core/interfaces/vehicleDocuments';
import { TrailerBrand } from 'src/app/core/interfaces/TrailerBrand';
import { Observable, of } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { UserState } from 'src/app/core/enums/user-state.enum';
import { VehicleMessages } from 'src/app/core/messages/vehicle-messages.enum';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { VehicleExtraDocument } from 'src/app/core/interfaces/vehicleExtraDocument';
import { DateManager } from 'src/app/core/managers/date.manager';
import { Company } from 'src/app/core/interfaces/company';
import { VehicleConfig } from 'src/app/core/interfaces/vehicleConfig';
import { Bank } from 'src/app/core/interfaces/bank';
import { VehicleCreate } from 'src/app/core/interfaces/vehicleCreate';
import { BodyWorkType } from 'src/app/core/models/body-work-type.model';
import { BasicResponse } from 'src/app/core/interfaces/basicResponse';
import { Trailer } from 'src/app/core/interfaces/trailer';
import { BankAccount } from 'src/app/core/interfaces/userBank';
import { ValidationRule } from 'src/app/core/interfaces/validation-rule';
import { FIELDS_SECURITY_STUDY, FIELDS_STATE } from 'src/app/core/config/user.config';
import { Roles } from 'src/app/core/enums/roles.enum';
import { Global } from 'src/app/core/resources/global';
import { CatalogItem } from 'src/app/core/interfaces/catalogItem';
import { UserValidate } from 'src/app/core/interfaces/userValidate';
import { Fmt } from 'src/app/core/messages/fmt';
import { FleetLoyalty } from 'src/app/core/interfaces/fleetLoyalty';
import { SelectableFleetLoyaltyTypes } from 'src/app/core/enums/selectableFleetTypes.enum';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { FirebaseApi } from 'src/app/core/classes/firebase-api';
import { DynamicFirebase } from 'src/app/core/classes/dynamic-firebase';
import { Filter, FilterModel } from 'src/app/core/models/filter';
import { FormControl } from '@angular/forms';
import { VehicleValidation } from 'src/app/core/interfaces/vehicleValidation';
import { VechileSoat } from 'src/app/core/interfaces/vehicleSoat';
import { VehicleRtm } from 'src/app/core/interfaces/vehicleRtm';
import { SatrackGpsLink, SatrackGpsLinkRetryResponse, SatrackVehicleStatusSyncResponse } from 'src/app/core/interfaces/satrack-gps-link';

export interface MandatoryDocument {
  value: string;
  name: string;
  mandatory: boolean;
}
@Injectable({
  providedIn: 'root'
})
export class VehiclesService {

  public paginationList: PaginationList = new PaginationList();
  vehicleSelected: Vehicle;
  data: string;
  unsubscribeVehicleChanges: Function;
  unsubscribeUserChanges: Function;
  unsubscribeOwnerChanges: Function;
  unsubscribeAdministratorChanges: Function;
  unsubscribeSatrackGpsLinkChanges: Function;
  private satrackGpsLinkWatchId = 0;
  private satrackGpsLinkWatchedVehicleId: string;
  private satrackGpsProviderNames: { companyId: string, names$: Observable<string[]> };
  documentSubscriptionUser: string;
  documentSubscriptionOwner: string;
  documentSubscriptionAdministrator: string;
  documentSubscriptionVehicle: string;
  public onChange: EventEmitter<Vehicle> = new EventEmitter();
  public onChangeUser: EventEmitter<User> = new EventEmitter();
  public onChangeOwner: EventEmitter<User> = new EventEmitter();
  public onChangeAdministrator: EventEmitter<User> = new EventEmitter();
  public onChangeSatrackGpsLink: EventEmitter<{ vehicleId: string, gpsLink: SatrackGpsLink }> = new EventEmitter();
  private userFieldsYYYYMMDD: string[] = ['expeditionDateId'];
  private userFieldsTZ: string[] = ['expeditionDate', 'expirationDate'];
  private userFieldsDDMMYYYY: string[] = ['checkExpeditionDate'];
  private userFieldsUTC0: string[] = ['date', 'creationDate'];
  private vehicleFieldsTZ: string[] = ['expirationDate'];
  private vehicleFieldsDDMMYYYY: string[] = ['checkExpeditionDate'];
  private vehicleFieldsUTC0: string[] = ['registrationDate', 'date', 'checkCreationDate'];


  constructor(
    private http: HttpClient,
    private endpointResources: Endpoints,
    private utils: Utils,
    private authService: AuthService,
    private stateUserPipe: StateUserPipe,
    private matDialog: MatDialog,
    private global: Global,
  ) { }

  /**
  * @returns {{value: string, name: string}[]} returns a list of mandatory documents
  * @description Gets a list of mandatory documents
  */
  async getMandatoryDocuments(): Promise<MandatoryDocument[]> {
    try {
      const docs: VehicleDocumentRequired[] = await this.getCompanyDocumentTypes(true).toPromise();
      if (!docs || !docs.length) return [];
      return docs.map(doc => { if (doc.active) return { value: doc.id, name: doc.name, mandatory: doc.mandatory } });
    } catch (e) {
      return [];
    }
  };

  getVehicleDocumentName(name: string): string {
    const dictionary = {
      frontPicture: "Fotografía frontal",
      leftLateralPicture: "Fotografía lateral izquierdo",
      rightLateralPicture: "Fotografía lateral derecho",
      propertyCard: "Tarjeta de propiedad"
    };
    if (dictionary[name]) return dictionary[name];
    return name;
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to check
  * @returns {VehicleExtraDocument[]} returns a list of vehicle's extra images
  * @description Gets a list of a vehicle's extra images
  */
  private getExtraImagesTypes(vehicle: Vehicle): VehicleExtraDocument[] {
    const extraImagesLoaded = [];
    if (vehicle && vehicle.extraImages) {
      vehicle.extraImages.forEach((extraDocument) => {
        extraImagesLoaded.push(extraDocument);
      });
    }
    return extraImagesLoaded;
  }

  /**
  * @returns {boolean} returns true if the user belongs to Teclogi, otherwise false
  * @description Checks if the user belongs to Teclogi
  */
  get isFromTeclogi(): boolean {
    return this.authService && this.authService.getCompany() && this.authService.getCompany().companyId && (this.authService.getCompany().companyId === environment.rootNit)
  }

  /**
  * @returns {Observable<Vehicle>} returns the vehicle expected if exists
  * @description Gets a vehicle from its license plate
  */
  public getValidationRules(): Observable<Array<ValidationRule>> {
    return this.http.get<Array<ValidationRule>>(
      environment.urlServerTeclogi + this.endpointResources.validationRules
    ).pipe(
      map((rules: ValidationRule[]) => {
        if (rules) {
          const roles = [Roles.ADMIN, Roles.DRIVER, Roles.OWNER];
          roles.forEach(role => {
            FIELDS_STATE[role] = [];
            FIELDS_SECURITY_STUDY[role] = [];
          })
          rules.forEach(rule => this.assign(rule));
        }
        return rules;
      })
    );
  }

  assign(validationRule: ValidationRule) {
    if (validationRule && validationRule.active) {
      if (validationRule.ruleType === 'state') {
        if (FIELDS_STATE[validationRule.roleName as Roles])
          FIELDS_STATE[validationRule.roleName as Roles].push(validationRule.ruleName);
      } else {
        if (FIELDS_SECURITY_STUDY[validationRule.roleName as Roles])
          FIELDS_SECURITY_STUDY[validationRule.roleName as Roles].push(validationRule.ruleName);
      }
    }
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to check
  * @param {'missing' | 'unaccepted'} mode is the state of the documents to check
  * @returns {Array<string>} returns a list of vehicle's documents unaccepted or missing
  * @description Gets a list of a vehicle's documents unaccepted or missing
  */
  public async getMissingDocuments(vehicle: Vehicle, mode: 'missing' | 'unaccepted' = 'missing', documents?: MandatoryDocument[]): Promise<Array<string>> {
    const extraImagesLoaded = this.getExtraImagesTypes(vehicle);
    const missingDocuments = [];
    const mandatoryDocuments = await this.checkDocumentRules(vehicle, documents);
    mandatoryDocuments.filter((doc) => {
      if (!doc.mandatory) return false;
      if (!doc.value) return false;
      if (this.global.listTypeDocuments.some(typeDoc => typeDoc.name === doc.value))
        return !(vehicle[doc.value] && vehicle[doc.value].length > 0);
      const extraImage = extraImagesLoaded.find(img => img.type && img.type.name === doc.name);
      if (extraImage) return mode === 'unaccepted' ? !extraImage.validated : !extraImage.path;
      return true;
    }).forEach(doc => missingDocuments.push(doc.name));
    return missingDocuments;
  }

  public async checkMissingInfo(vehicle: Vehicle, driver: User, documents?: MandatoryDocument[]): Promise<{ missingDocuments: string[], unacceptedDocuments: string[], invalidReferences: string[] }> {
    const mandatoryDocuments = documents && documents.length ? documents : await this.getMandatoryDocuments();
    const missingDocuments = await this.getMissingDocuments(vehicle, 'missing', mandatoryDocuments);
    const unacceptedDocuments = await this.getMissingDocuments(vehicle, 'unaccepted', mandatoryDocuments);
    const invalidReferences = this.invalidReferences(driver);
    return {
      missingDocuments: missingDocuments && missingDocuments.length > 0 ? missingDocuments : [],
      unacceptedDocuments: unacceptedDocuments && unacceptedDocuments.length > 0 ? unacceptedDocuments : [],
      invalidReferences: invalidReferences && invalidReferences.length > 0 ? invalidReferences : []
    };
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to check
  * @returns {{ value: string, name: string }[]} returns a list of vehicle's mandatoy documents with or without RTM as mandatory
  * @description Gets a list of a vehicle's mandatory documents with or without RTM as mandatory
  */
  public async checkDocumentRules(vehicle: Vehicle, documents?: MandatoryDocument[]): Promise<{ value: string, name: string, mandatory: boolean }[]> {
    const mandatoryDocuments = documents && documents.length ? documents : await this.getMandatoryDocuments();
    const documentsToCheck = mandatoryDocuments.find((document) => document.name === 'RTM');
    try {
      if (documentsToCheck) {
        const position = mandatoryDocuments.findIndex((document) => document.name === 'RTM');
        const expirationDate = DateManager.stringToDate(vehicle.validRTM.expirationDate, 'YYYY-MM-DD ZZ').getTime();
        const now = new Date().getTime();
        if (expirationDate > now)
          mandatoryDocuments[position].mandatory = false;
      }
    } catch (e) { }
    return mandatoryDocuments;
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to check
  * @returns {boolean} returns true if the vehicle has unaccepted documents, otherwise false
  * @description Checks if the vehicle has unaccepted documents
  */
  public async documentsValidator(vehicle: Vehicle): Promise<boolean> {
    let docs = await this.getMissingDocuments(vehicle, 'unaccepted');
    return docs.length == 0;
  }

  /**
  * @param {User} driver is the driver to check
  * @returns {Array<string>} returns a list of driver's invalid references (not validated)
  * @description Gets a list of a driver's invalid references (not validated)
  */
  public invalidReferences(driver: User): Array<string> {
    let invalidReferences = [];
    if (!driver || !driver.emergencyContact || !driver.emergencyContact.validated) invalidReferences.push("Contacto de emergencia");
    if (!driver || !driver.referenceLaboral || !driver.referenceLaboral.validated) invalidReferences.push("Referencia laboral");
    if (!driver || !driver.referencePersonal || !driver.referencePersonal.validated) invalidReferences.push("Referencia personal");
    return invalidReferences;
  }

  /**
  * @param {string} companyId is the vehicle's company id
  * @param {string} holderCompanyId is the vehicle's holder company id
  * @param {number} pageKey is the key of the expected page
  * @param {number} pageSize is the size of the expected page
  * @param {string} id is the vehicle's id
  * @param {string} state is the vehicle's bank account state
  * @param {boolean} vehicleState is the vehicle's state
  * @returns {Observable<Vehicle[]>} returns a list of vehicles filtered
  * @description Gets a list of a vehicles filtered
  */
  public getListVehicles(
    pageKey: number,
    pageSize: number,
    filters: string
  ): Observable<Vehicle[]> {
    let params = this.getParamsFormatted(filters, pageKey, pageSize);
    return this.http.get<Vehicle[]>(`${environment.urlServerTeclogi}${this.endpointResources.getVehiclesFromAllCompanies}`, { params });
  }

  public getVehiclesByCompany(options: {
    pageKey?: number;
    pageSize?: number;
    plate?: string;
    ministryRetry?: boolean;
    ministryManualAdjustment?: boolean;
  }): Observable<Vehicle[]> {
    let params = new HttpParams()
      .set('companyId', this.getUserCompanyId());
    if (this.utils.isDefined(options.pageKey)) {
      params = params.set('pageKey', options.pageKey.toString());
    }
    if (options.pageSize) {
      params = params.set('pageSize', options.pageSize.toString());
    }
    const plate = options.plate ? options.plate.trim().toUpperCase() : '';
    if (plate) {
      params = params.set('id', plate);
    }
    if (this.utils.isDefined(options.ministryRetry)) {
      params = params.set('ministryRetry', options.ministryRetry.toString());
    }
    if (this.utils.isDefined(options.ministryManualAdjustment)) {
      params = params.set('ministryManualAdjustment', options.ministryManualAdjustment.toString());
    }
    return this.http.get<Vehicle[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.getVehiclesByOwnCompany}`,
      { params }
    );
  }

  getParamsFormatted(paramsFilter: string, pageKey?: number, pageSize?: number): HttpParams {
    let params = new HttpParams();
    if (paramsFilter) {
      paramsFilter.split('&').forEach(param => {
        if (param) {
          const [key, value] = param.split('=');
          if (key && value) {
            params = params.append(key, value);
          }
        }
      });
    }
    if (pageKey && pageSize && !paramsFilter.includes("id")) {
      params = params.append('pageKey', pageKey.toString());
      params = params.append('pageSize', pageSize.toString());
    }
    if (!paramsFilter.includes("holderCompany"))
      params = params.append('holderCompany', this.getUserCompanyId());
    return params;
  }

  getAccountState(state: string): string {
    let url = '';
    if (this.utils.isDefined(state) && state.length) {
      switch (state) {
        case '0':
          url
          break
        case '1':
          url += '&stateAccountAdvance=true';
          break
        case '2':
          url += '&stateAccountBalance=true';
          break
        case '3':
          url += '&stateAccountAdvance=true&stateAccountBalance=true'
          break
        case '4':
          url += '&stateAccountAdvance=false&stateAccountBalance=false'
          break
        case '5':
          url += '&stateAccountAdvance=false'
          break
        case '6':
          url += '&stateAccountBalance=false'
          break
      }
    }
    return url;
  }

  /**
  * @param {string} id is the vehicle's license plate
  * @returns {Observable<Vehicle>} returns the vehicle expected if exists
  * @description Gets a vehicle from its license plate ignoring the company
  */
  public getVehicleFromAllCompanies(id: string): Observable<Vehicle> {
    return this.http.get<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.getVehicleFromAllCompanies + id
    );
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @description Requests the latest registration status from Satrack for a vehicle
  */
  public syncSatrackVehicleRegistrationStatus(licensePlate: string): Observable<SatrackVehicleStatusSyncResponse> {
    return this.http.post<SatrackVehicleStatusSyncResponse>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.syncSatrackVehicleRegistrationStatus, encodeURIComponent(licensePlate))}`,
      null
    );
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @returns {Observable<SatrackGpsLinkRetryResponse>} Satrack's answer, a 2xx can still be a rejection
  * @description Requests Satrack to retry the vehicle's GPS link, the process runs asynchronously on Satrack's side
  */
  public retrySatrackVehicleGpsLink(licensePlate: string): Observable<SatrackGpsLinkRetryResponse> {
    return this.http.post<SatrackGpsLinkRetryResponse>(
      `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.retrySatrackVehicleGpsLink, encodeURIComponent(licensePlate))}`,
      null
    );
  }

  /**
  * @returns {Observable<string[]>} the names of the GPS providers Satrack's catalog lists, or null when it can't be read
  * @description Satrack's provider catalog barely changes, so it's read once per company and session
  */
  public getSatrackGpsProviderNames(): Observable<string[]> {
    const companyId = this.getUserCompanyId();
    if (!this.satrackGpsProviderNames || this.satrackGpsProviderNames.companyId !== companyId) {
      const names$ = this.http.get<{ data?: Array<{ id: number, name: string }> }>(
        `${environment.urlServerTeclogi}${this.endpointResources.satrackGpsProviders}`
      ).pipe(
        map((response) => ((response && response.data) || []).map((provider) => provider && provider.name).filter((name) => !!name)),
        // A failed read is kept too: the check only informs, so it isn't worth asking Satrack again on every start
        catchError(() => of(null)),
        shareReplay(1)
      );
      this.satrackGpsProviderNames = { companyId, names$ };
    }
    return this.satrackGpsProviderNames.names$;
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to check
  * @returns {Observable<boolean>} true when its GPS belongs to one of Satrack's providers but it has no link recorded
  * @description Same rule the backend uses to reconcile the link when the vehicle starts a service
  */
  public hasUnlinkedSatrackGps(vehicle: Vehicle): Observable<boolean> {
    const gpsType = vehicle && !vehicle.satrackGpsLink && vehicle.gps && vehicle.gps.gpsType;
    if (!gpsType) return of(false);
    return this.getSatrackGpsProviderNames().pipe(
      map((names) => !!names && names.some((name) => name.trim().toLowerCase() === gpsType.trim().toLowerCase()))
    );
  }

  /**
  * @param {string} id is the vehicle's license plate
  * @returns {Observable<Vehicle>} returns the vehicle expected if exists
  * @description Gets a vehicle from its license plate from the own company
  */
  public getVehicleFromOwnCompany(id: string): Observable<Vehicle> {
    const holderCompany = this.getUserCompanyId();
    let url = `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.getVehiclesByIdFromOwnCompany, id, holderCompany)}`;
    return this.http.get<Vehicle[]>(url).pipe(map(response => response && response.length > 0 ? response[0] : null));
  }

  /**
  * @param {string} id is the vehicle's license plate
  * @returns {Observable<Vehicle[]>} returns a list of vehicles from the own company
  * @description Gets a list of vehicles from the own company ignoring the company
  */
  public getVehiclesFromOwnCompany(id: string): Observable<Vehicle[]> {
    const holderCompany = this.getUserCompanyId();
    let url = `${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.getVehiclesByIdFromOwnCompany, id, holderCompany)}`;
    return this.http.get<Vehicle[]>(url);
  }

  public getUserCompanyId(): string {
    const session = this.authService.getUserSession();
    if (session && session.clientCompanyId) return session.clientCompanyId;
    const company = this.authService.getCompanySaaS();
    return company && company.companyId ? company.companyId : '';
  }

  /**
  * @returns {Observable<Bank[]>} returns a list of banks registered
  * @description Gets a list of banks registered
  */
  public getListBank(): Observable<Bank[]> {
    this.data = environment.urlServerTeclogi + this.endpointResources.getBanck;
    return this.http.get<Bank[]>(this.data);
  }

  /**
  * @param {Vehicle} dataVehicleBank is the vehicle to register its bank account
  * @returns {Observable<Vehicle>} returns the vehicle updated
  * @description Updates a vehicle bank account
  */
  public sendDataRegisterBankAccount(dataVehicleBank: Vehicle): Observable<Vehicle> {
    return this.http.post<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.registerBanckAccount,
      dataVehicleBank
    );
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {string} reason is the reason to disable the current vehicle
  * @returns {Observable<string>} returns a message indicating the result of the process
  * @description Disables a vehicle
  */
  public disableVehicle(licensePlate: string, reason: string): Observable<string> {
    return this.http.post<string>(
      environment.urlServerTeclogi + this.endpointResources.disableVehicle + licensePlate, { message: reason }
    );

  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {boolean} approval indicates if the document must to be approved or not
  * @param {'advance'|'balance'} paymentType is the type of the payment
  * @param {string} reason is the reason to approve the documents
  * @returns {Observable<Vehicle>} returns the vehicle updated
  * @description Approve a vehicle's documents
  */
  approveDocuments(licensePlate: string, approval: boolean, paymentType: 'advance' | 'balance', reason: string): Observable<Vehicle> {
    let params = new HttpParams();
    params = params.append('approval', approval.toString());
    const body = {
      licensePlate,
      paymentType,
      reason: (reason || ''),
      userId: this.authService.getUserSession().information.document
    };
    return this.http.post<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.approveDocumentsVehicle,
      body,
      { params }
    );
  }

  /**
  * @param data is the body to create the vehicle, driver, owner and admin
  * @returns {Observable<VehicleCreate>} returns the vehicle created
  * @description Creates a vehicle and its driver, owner and admin if is required
  */
  public createVehicleDriverOwner(data): Observable<VehicleCreate> {
    return this.http.post<VehicleCreate>(
      `${environment.urlServerTeclogi}${this.endpointResources.createVehicleDriverOwner}`,
      data
    );
  }

  public createVehicleOwner(data: VehicleCreate): Observable<VehicleCreate> {
    return this.http.post<VehicleCreate>(
      `${environment.urlServerTeclogi}${this.endpointResources.createVehicleOwner}`,
      data
    );
  }

  /*public getTrailerConfig() {
    return this.http.get(
      `${environment.urlServerTeclogi}${this.endpointResources.urlCatalogTrailerConfig}`
    );
  }*/

  /**
  * @param {number} vehicletype is the code of the vehicle's class selected
  * @returns {Observable<BodyWorkType[]>} returns a list of bodywork types
  * @description Gets a list of bodyworks types by vehicle's class
  */
  public getBodyWork(vehicletype: number): Observable<BodyWorkType[]> {
    return this.http.get<BodyWorkType[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.catalogBodywork}?${this.endpointResources.vehicleType}=${vehicletype}`
    );
  }

  /**
  * @returns {Observable<TrailerBrand>} returns a catalog of trailer brands
  * @description Gets a catalog of trailer brands
  */
  public getTrailerBrand(): Observable<TrailerBrand> {
    return this.http.get<TrailerBrand>(
      `${environment.urlServerTeclogi}${this.endpointResources.urlCatalog}/TrailerBrand`
    )
  }

  /**
  * @param {number} axles are the number of vehicle's axles
  * @returns {Observable<VehicleConfig[]>} returns a list of vehicle config
  * @description Gets a list of vehicle config
  */
  public getVehicleConfig(axles: number, name?: string, trailersOnly?: boolean): Observable<VehicleConfig[]> {
    let params = new HttpParams();
    params = params.append('axles', axles.toString());
    if (name) params = params.append('name', name);
    if (trailersOnly) params = params.append('trailersOnly', trailersOnly.toString());
    return this.http.get<VehicleConfig[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.vehicleConfiguration}`,
      { params }
    );
  }

  /**
  * @param data is the body to create the trailer
  * @returns {Observable<BasicResponse>} returns the result of the process
  * @description Creates a trailer
  */
  public createTrailer(data): Observable<Trailer & BasicResponse> {
    return this.http.post<Trailer & BasicResponse>(
      `${environment.urlServerTeclogi}${this.endpointResources.createTrailer}`,
      data
    );
  }

  /**
  * @param {{name:string, id:string}[]} data are the list of gps to create
  * @returns {Observable<BasicResponse>} returns the result of the process
  * @description Creates a list of gps
  */
  public createGPS(data: { name: string, id: string }[]): Observable<BasicResponse> {
    var headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');

    return this.http.post<BasicResponse>(
      environment.urlServerTeclogi + this.endpointResources.createGps,
      data,
      { headers, responseType: 'text' as 'json' }
    );
  }

  /**
  * @param {string} vehicle is the vehicle's license plate
  * @param {string} id is the trailer's license plate
  * @returns {Observable<Trailer[]>} returns the trailer expected as the first element of array
  * @description Gets a trailer from its license plate and its vehicle's license plate as the first element of array
  */
  public detailTrailer(vehicle: string, id: string): Observable<Trailer[]> {
    return this.http.get<Trailer[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.listTrailer}vehicle=${vehicle}&id=${id}`
    );
  }

  /**
  * @param {Trailer} data is the trailer to check
  * @returns {Trailer} returns the trailer modified
  * @description Gets the trailer modified deleting address and municipalityCode to owner and admin with NIT
  */
  public getTrailerObject(data: Trailer): Trailer {
    const dataTrailer = this.utils.clone(data);
    if (data && data.owner && data.owner.documentTypeId != '3') {
      delete dataTrailer.owner.municipalityCode;
      delete dataTrailer.owner.address;
    }
    if (data && data.administrator && data.administrator.documentTypeId != '3') {
      delete dataTrailer.administrator.municipalityCode;
      delete dataTrailer.administrator.address;
    }
    return dataTrailer;
  }

  /**
  * @param {string} filter are the filters applied
  * @returns {Observable<Trailer[]>} returns a list of trailers
  * @description Gets a list of trailers filtered
  */
  public getTrailerList(filter?: string): Observable<Trailer[]> {
    return this.http.get<Trailer[]>(
      `${environment.urlServerTeclogi}${this.endpointResources.getTrailerList}${filter}`
    );

  }

  /**
  * @param {string} id is the trailer's license plate
  * @returns {Observable<Trailer>} returns the trailer detail
  * @description Gets a single trailer's detail by its license plate (incluye las banderas RNDC: ministryRetry, ministryContingency, etc.)
  */
  public getTrailerDetail(id: string): Observable<Trailer> {
    return this.http.get<Trailer>(
      `${environment.urlServerTeclogi}${this.endpointResources.getTrailerDetail}${id}`
    );
  }

  /**
  * @param {Trailer} trailer is the complete trailer
  * @param {string} licencePlate is the vehicle's license plate
  * @returns {Observable<BasicResponse>} returns the result of the process
  * @description Updates a vehicle's trailer
  */
  public updateTrailer(trailer: Trailer, licencePlate: string) {
    return this.http.post<BasicResponse>(
      `${environment.urlServerTeclogi}${this.endpointResources.updateTrailer}${licencePlate}`,
      trailer
    );

  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {User} driver is the driver to set
  * @returns {Observable<User>} returns the driver updated
  * @description Changes a vehicle's driver
  */
  public vehicleChangeDriver(licensePlate: string, driver: User, securityStudyValidation: boolean): Observable<User> {
    let params = new HttpParams();
    params = params.append('licensePlate', licensePlate);
    params = params.append('validateTruora', securityStudyValidation.toString());
    return this.http.post<User>(
      `${environment.urlServerTeclogi}${this.endpointResources.vehicleChangeDriver}`,
      driver,
      { params }
    );
  }

  /**
  * @param {BankAccount[]} data is the list of bank accounts of vehicles
  * @param {'balance' | 'advance'} type is the type of the bank account
  * @returns {Observable<{body}>} returns a file with the respective inscription account
  * @description Gets a file with the respective inscription account
  */
  public getFileForInscriptionAccount(data: { licensePlate: string, accountType: ('balance' | 'advance')[] }[]): Observable<{ body }> {
    var url = environment.urlServerTeclogi + this.endpointResources.setVehicleBankAccount;
    return this.http.post(
      url,
      data,
      {
        responseType: 'blob',
        observe: 'response'
      }
    ).pipe(
      timeout(960000)
    );
  }

  /**
  * @param {User} body is the user to set as administrator
  * @param {string} licensePlate is the vehicle's license plate
  * @returns {Observable<Vehicle>} returns the vehicle updated with new admin associated
  * @description Updates a vehicle's admin
  */
  public updateAdministrator(body: User, licensePlate: string): Observable<Vehicle> {
    if (body && body.information && body.information.documentTypeId != '3') {
      if (body.municipalityCode)
        delete body.municipalityCode;
      if (body.address)
        delete body.address;
    }

    return this.http.post<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.updateAdministrator + licensePlate,
      body
    );
  }

  /**
  * @description Resets the paginationList values
  */
  resetPaginationList() {
    this.paginationList.setEnablePagingScroll(true);
    this.paginationList.setList([]);
    this.paginationList.setPageKey(1);
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to get the state
  * @returns {string} returns the vehicle's state transformed if exists
  * @description Gets and transforms the vehicle's state to be shown.
  */
  getVehicleState(vehicle: Vehicle): string {
    if (!this.utils.isEmpty(vehicle.state) && !this.utils.isEmpty(vehicle.state.active)) {
      return this.stateUserPipe.transform(vehicle.state.description);
    }
    return this.stateUserPipe.transform(UserState.PENDING);
  }

  /**
  * @param {string} idVehicle is the vehicle's license plate
  * @param {{userId: string, userName: string}} data is the data to approve the vehicle
  * @returns {Observable<Vehicle>} returns the vehicle approved
  * @description Approves a vehicle
  */
  approveVehicle(idVehicle: string, data: { userId: string, userName: string }): Observable<Vehicle> {
    return this.http.post<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.approveVehicle + idVehicle,
      data
    );
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {{userId: string, userName: string}} data is the user who forces the activation
  * @returns {Observable<Vehicle>} returns the vehicle activated
  * @description Forces the vehicle activation, writing only its state so the rest of the vehicle stays as stored
  */
  forceVehicleActivation(licensePlate: string, data: { userId: string, userName: string }): Observable<Vehicle> {
    return this.http.put<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.forceVehicleActivation + licensePlate,
      data
    );
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {VehicleDocuments} body is vehicle's document to change
  * @returns {Observable<Vehicle>} returns the vehicle updated
  * @description Updates the vehicle's documents
  */
  public updateVehicleDocuments(licensePlate: string, body: VehicleDocuments): Observable<Vehicle> {
    let params = new HttpParams();
    params = params.append('licensePlate', licensePlate);
    return this.http.post<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.updateDocumentsVehicle,
      body,
      { params }
    );
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {Gps} body is vehicle's gps to change
  * @returns {Observable<Vehicle>} returns the vehicle updated
  * @description Updates the vehicle's gps
  */
  public updateGpsDocuments(licensePlate: string, body: Gps): Observable<Vehicle> {
    let params = new HttpParams();
    params = params.append('licensePlate', licensePlate);
    return this.http.post<Vehicle>(
      environment.urlServerTeclogi + this.endpointResources.updateGpsVehicle,
      body,
      { params }
    );
  }

  /**
  * @returns {Observable<{catalog: VehicleDocumentCatalog[]}>} returns the document types list
  * @description Gets the document types list
  */
  public getDocumentTypes(): Observable<{ catalog: VehicleDocumentCatalog[] }> {
    return this.http.get<{ catalog: VehicleDocumentCatalog[] }>(
      `${environment.urlServerTeclogi}${this.endpointResources.vehicleDocumentTypes}`
    );
  }

  /**
  * @returns {Observable<VehicleDocumentRequired[]>} returns the document types list configured
  * @description Gets the document types list configured
  */
  public getCompanyDocumentTypes(active?: boolean): Observable<VehicleDocumentRequired[]> {
    let params = new HttpParams();
    if (this.utils.isDefined(active))
      params = params.append('active', active.toString());
    return this.http.get<VehicleDocumentRequired[]>(
      `${environment.urlServerTeclogi}company/saas/${this.authService.getCompanySaaS().companyId}/vehicle-documents`,
      { params }
    );
  }

  public getCompanyFleetTypes(): Observable<{ catalog: { id: string, name: SelectableFleetLoyaltyTypes }[] }> {
    return this.http.get<{ catalog: { id: string, name: SelectableFleetLoyaltyTypes }[] }>(`${environment.urlServerTeclogi}${this.endpointResources.getFleetLoyaltyTypes}`);
  }

  public getCompanyFleetLoyalties(): Observable<FleetLoyalty[]> {
    return this.http.get<FleetLoyalty[]>(`${environment.urlServerTeclogi}${this.endpointResources.companyFleetLoyalty}`);
  }

  public createCompanyFleetLoyalty(fleetLoyalties: FleetLoyalty[]) {
    return this.http.post(`${environment.urlServerTeclogi}${this.endpointResources.companyFleetLoyalty}`, fleetLoyalties);
  }
  public updateCompanyFleetLoyalty(fleetLoyalties: FleetLoyalty[]) {
    return this.http.put(`${environment.urlServerTeclogi}${this.endpointResources.companyFleetLoyalty}`, fleetLoyalties);
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {VehicleExtraDocument} extraImage is new vehicle's extra image
  * @returns {Observable<BasicResponse>} returns the result of the update process
  * @description Updates a vehicle's extra images
  */
  public saveExtraDocs(licensePlate: string, extraImage: VehicleExtraDocument): Observable<BasicResponse> {
    return this.http.post<BasicResponse>(
      `${environment.urlServerTeclogi}${this.endpointResources.vehicleDocuments}?licensePlate=${licensePlate}`,
      extraImage
    );
  }

  public validateExpirationSOAT(vehicle: Vehicle): { valid: boolean, errors: VehicleMessages[], expirationDate?: Date } {
    const errors: VehicleMessages[] = [];
    let expirationDate: Date;
    const validSOAT: VechileSoat = this.utils.getNestedValue(vehicle, 'validSOAT');
    if (!validSOAT) return { valid: false, errors: [VehicleMessages.SOAT_NO_INFORMATION] };
    if (!validSOAT.companyName) errors.push(VehicleMessages.SOAT_INSURANCE_COMPANY_REQUIRED);
    if (!validSOAT.number) errors.push(VehicleMessages.SOAT_POLICY_NUMBER_REQUIRED);
    if (!validSOAT.expirationDate) errors.push(VehicleMessages.SOAT_NO_EXPIRATION);
    else {
      expirationDate = DateManager.stringToDate(validSOAT.expirationDate, 'YYYY-MM-DD');
      if (DateManager.isSameOrBefore(expirationDate, new Date()))
        errors.push(VehicleMessages.SOAT_EXPIRED_NOW);
    }
    return { valid: errors.length === 0, errors, expirationDate };
  }

  public validateExpirationRTM(vehicle: Vehicle): { valid: boolean, errors: VehicleMessages[], expirationDate?: Date, isExonerated?: boolean } {
    const errors: VehicleMessages[] = [];
    let expirationDate: Date;
    const { isExonerated } = this.utils.isVehicleExoneratedByRtm(vehicle);
    if (isExonerated) return { valid: true, errors: [VehicleMessages.RTM_EXONERATED], isExonerated };
    if (vehicle && vehicle.manufacturingYear && DateManager.isBefore(new Date(), DateManager.add(vehicle.manufacturingYear, 1, 'years', 'YYYY')))
      return { valid: true, errors: [VehicleMessages.RTM_POSSIBLE_EXONERATED], isExonerated };
    const validRTM: VehicleRtm = this.utils.getNestedValue(vehicle, 'validRTM');
    if (!validRTM) return { valid: false, errors: [VehicleMessages.RTM_NO_INFORMATION] };
    if (!validRTM.expirationDate) errors.push(VehicleMessages.RTM_NO_EXPIRATION);
    else {
      expirationDate = DateManager.stringToDate(validRTM.expirationDate, 'YYYY-MM-DD');
      if (DateManager.isSameOrBefore(expirationDate, new Date()))
        errors.push(VehicleMessages.RTM_EXPIRED_NOW);
    }
    return { valid: errors.length === 0, errors, expirationDate, isExonerated };
  }

  /**
  * @param {string} licensePlate is the vehicle's license plate
  * @param {Date} from is the date to compare
  * @param {Company} cargoCompany is the company of the cargo to check
  * @returns {Observable<VehicleMessages>} returns the list of vehicle's errors and alerts from SOAT and RTM expiration
  * @description Gets a list of vehicle's errors and alerts from SOAT and RTM expirations
  */
  public validateExpirationRTMSOAT(licensePlate: string, lastDestinationDate: Date, cargoCompany: Company): Observable<VehicleMessages> {
    return new Observable(
      observer => {
        const $ = this.getVehicleFromOwnCompany(licensePlate).subscribe(
          (vehicle: Vehicle) => {
            const { valid: validSOAT, errors: errorsSOAT, expirationDate: expirationDateSOAT } = this.validateExpirationSOAT(vehicle);
            if (!validSOAT && errorsSOAT.length) errorsSOAT.forEach(error => observer.next(error));
            // SOAT
            if (expirationDateSOAT) {
              const daysToAlertExpirationSOAT = cargoCompany && cargoCompany.daysToAlertExpirationSOAT ? cargoCompany.daysToAlertExpirationSOAT : VehicleMessages.DAYS_TO_ALERT_EXPIRATION_DEFAULT;
              const daysToBlockSOAT = cargoCompany && cargoCompany.daysToBlockSOAT ? cargoCompany.daysToBlockSOAT : VehicleMessages.DAYS_TO_BLOCK_DEFAULT;
              const soatAlertsAndBlocksEnabled = cargoCompany && this.utils.isDefined(cargoCompany.soatAlertsAndBlocksEnabled) ? cargoCompany.soatAlertsAndBlocksEnabled : true;

              if (DateManager.isBefore(lastDestinationDate, expirationDateSOAT) && !soatAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.SOAT_VALID);
              else if (DateManager.isBefore(DateManager.add(lastDestinationDate, daysToAlertExpirationSOAT, 'days'), expirationDateSOAT) && soatAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.SOAT_MORE_N_DAYS);
              else if (DateManager.isBefore(DateManager.add(lastDestinationDate, daysToBlockSOAT, 'days'), expirationDateSOAT) && soatAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.SOAT_LESS_ALERT_N_DAYS);
              else if (DateManager.isBefore(lastDestinationDate, expirationDateSOAT) && soatAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.SOAT_LESS_BLOCK_N_DAYS);
              else
                observer.next(VehicleMessages.SOAT_EXPIRATED);
            }
            // RTM
            const {
              valid: validRTM,
              errors: errorsRTM,
              expirationDate: expirationDateRTM,
              isExonerated: isExoneratedRTM
            } = this.validateExpirationRTM(vehicle);
            if (errorsRTM.length) errorsRTM.forEach(error => observer.next(error));
            if (!isExoneratedRTM && expirationDateRTM) {
              const daysToAlertExpirationRTM = cargoCompany && cargoCompany.daysToAlertExpirationRTM ? cargoCompany.daysToAlertExpirationRTM : VehicleMessages.DAYS_TO_ALERT_EXPIRATION_DEFAULT;
              const daysToBlockRTM = cargoCompany && cargoCompany.daysToBlockRTM ? cargoCompany.daysToBlockRTM : VehicleMessages.DAYS_TO_BLOCK_DEFAULT;
              const rtmAlertsAndBlocksEnabled = cargoCompany && this.utils.isDefined(cargoCompany.rtmAlertsAndBlocksEnabled) ? cargoCompany.rtmAlertsAndBlocksEnabled : true;

              if (DateManager.isBefore(lastDestinationDate, expirationDateRTM) && !rtmAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.RTM_VALID);
              else if (DateManager.isBefore(DateManager.add(lastDestinationDate, daysToAlertExpirationRTM, 'days'), expirationDateRTM) && rtmAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.RTM_MORE_N_DAYS);
              else if (DateManager.isBefore(DateManager.add(lastDestinationDate, daysToBlockRTM, 'days'), expirationDateRTM) && rtmAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.RTM_LESS_ALERT_N_DAYS);
              else if (DateManager.isBefore(lastDestinationDate, expirationDateRTM) && rtmAlertsAndBlocksEnabled)
                observer.next(VehicleMessages.RTM_LESS_BLOCK_N_DAYS);
              else
                observer.next(VehicleMessages.RTM_EXPIRATED);
            }
            $.unsubscribe();
            observer.complete();
          }
        );
      }
    );
  }

  /**
  * @param {string} phone is the user's phone
  * @returns {Observable<User[]>} returns the list of users with the same phone
  * @description Gets the list of users with the same phone
  */
  public getUsersByPhone(phone: string): Observable<User[]> {
    return this.http.get<User[]>(`${environment.urlServerTeclogi}${this.endpointResources.usersList}?phone=${phone}`);
  }

  /**
  * @param {string} phone is the phone number to check
  * @returns {Observable<Vehicle>} returns the phone without "57" prefix
  * @description Deletes a "57" phone prefix if exists
  */
  removeCountryCode(phone: string): string {
    return phone.startsWith('57') ? phone.slice(2) : phone;
  }

  /**
  * @param {string} phone is the phone number to check
  * @returns {Observable<Vehicle>} returns the phone with "57" prefix
  * @description Adds a "57" phone prefix if is necessary
  */
  addCountryCode(phone: string): string {
    return phone.startsWith('57') ? phone : `57${phone}`;
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to update
  * @returns {Observable<BasicResponse>} returns the result of the process
  * @description Updates a vehicle
  */
  updateVehicle(vehicle: Vehicle): Observable<BasicResponse> {
    return this.http.post<BasicResponse>(`${environment.urlServerTeclogi}${this.endpointResources.getVehicleFromAllCompanies}`, vehicle);
  }

  public requestValidation(vehicle: VehicleValidation): Observable<BasicResponse> {
    return this.http.post<BasicResponse>(`https://hubspot-tickets-vehicles-252798014361.us-east1.run.app/tickets`, vehicle);
  }

  /**
  * @param {Vehicle} vehicle is the vehicle to update
  * @returns {Observable<BasicResponse>} returns the result of the process
  * @description Updates a vehicle's fields
  */
  updateVehicleFields(vehicle: Vehicle): Observable<BasicResponse> {
    return this.http.put<BasicResponse>(`${environment.urlServerTeclogi}${this.endpointResources.updateVehicleFields}`, vehicle);
  }

  /**
  * @param {User} user is the user to update
  * @returns {Observable<BasicResponse>} returns the result of the process
  * @description Updates a user's licenses
  */
  updateUserLicenses(document: string, user: User, role: string): Observable<BasicResponse> {
    let params = new HttpParams();
    params = params.append('role', role);
    return this.http.put<BasicResponse>(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.updateUserLicenses, document)}`, user, { params });
  }

  /*public createThirdParty(typeBankAccount: string, licensePlate: string, data: any) {
    let params = new HttpParams();

    params = params.append('type', typeBankAccount);
    params = params.append('licensePlate', licensePlate);

    return this.http.post(
      environment.urlServerTeclogi + this.endpointResources.createThirdPartyBank,
      data,
      { params }
    )
  }*/

  /**
  * @param {string} id is the vehicle's license plate
  * @param {boolean} state indicates if the vehicle has to be activated or not
  * @returns {Observable<BasicResponse>} returns the result of the process
  * @description Updates a vehicle's state
  */
  public setStateVehicle(id: string, state: boolean): Observable<BasicResponse> {
    let data = {
      id,
      state: {
        active: state,
        description: state ? 'Active' : 'Inactive'
      }
    }
    return this.http.post<BasicResponse>(`${environment.urlServerTeclogi}${this.endpointResources.stateVehicle}/${id}`, data)
  }

  public getVehicleTypes() {
    return this.http.get<{ catalog: CatalogItem[] }>(`${environment.urlServerTeclogi}${this.endpointResources.vehicleTypeCatalog}`);
  }

  public basicCheck(id: string): Observable<UserValidate> {
    return this.http.post<UserValidate>(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.basicCheck + '?forceCreation=true', id)}`, {});
  }

  public rndcVehicleCreation(id: string): Observable<Vehicle> {
    return this.http.post<Vehicle>(`${environment.urlServerTeclogi}${Fmt.string(this.endpointResources.rndcVehicleCreation, id)}`, {});
  }

  public async watch(vehicleId: string) {
    await DynamicFirebase.configFirebaseByCompanyId('default');
    const firestore = DynamicFirebase.getFirestoreByCompanyId('default');
    this.unsubscribeVehicleChanges = firestore.collection('Vehicle').doc(vehicleId).onSnapshot((snapshot) => {
      const vehicle = snapshot.data() as Vehicle;
      if (!vehicle || !vehicle.id) return;
      const vehicleSanitized = this.sanitizeVehicle(vehicle);
      this.onChange.emit(vehicleSanitized);
    });
  }

  private sanitizeVehicle(vehicle: Vehicle): Vehicle {
    if (!vehicle) return null;
    Object.keys(vehicle).forEach(key => {
      if (vehicle[key] === null || vehicle[key] === undefined)
        delete vehicle[key];
      else if (typeof vehicle[key] === 'object') {
        if (this.utils.isDefined(vehicle[key].seconds) && this.utils.isDefined(vehicle[key].nanoseconds)) {
          const date = new Date(vehicle[key].seconds * 1000 + vehicle[key].nanoseconds / 1000000);
          if (this.vehicleFieldsUTC0.includes(key))
            vehicle[key] = DateManager.utcTransform(date);
          else if (this.vehicleFieldsDDMMYYYY.includes(key))
            vehicle[key] = DateManager.dateToString(date, 'DD/MM/YYYY');
          else if (this.vehicleFieldsTZ.includes(key))
            vehicle[key] = DateManager.dateToString(date, 'YYYY-MM-DDTHH:MM:SSZ');
          else
            vehicle[key] = DateManager.dateToString(date);
        }
        vehicle[key] = this.sanitizeVehicle(vehicle[key]);
        if (Object.keys(vehicle[key]).length === 0)
          delete vehicle[key];
      }
    });
    return vehicle as Vehicle;
  }

  public stopWatch() {
    if (this.unsubscribeVehicleChanges) {
      this.unsubscribeVehicleChanges();
      this.unsubscribeVehicleChanges = null;
    }
  }

  /**
  * @param {string} vehicleId is the vehicle's license plate
  * @description Listens the vehicle's "satrackGpsLink", which the backend writes asynchronously after the vehicle
  * or its GPS is saved. Teclogi keeps it in its own Firestore and every other company in the shared one, same rule as
  * the backend's SatrackGpsLinkStorage. Emits null when the vehicle has no link
  */
  public async watchSatrackGpsLink(vehicleId: string) {
    this.stopWatchSatrackGpsLink();
    this.satrackGpsLinkWatchedVehicleId = vehicleId;
    const watchId = this.satrackGpsLinkWatchId;
    const onSnapshot = (snapshot) => {
      // Only the link is sanitized: any change to the shared document fires this listener
      const storedLink = snapshot.get('satrackGpsLink');
      const vehicle = storedLink ? this.sanitizeVehicle({ satrackGpsLink: storedLink } as Vehicle) : null;
      const gpsLink = vehicle && vehicle.satrackGpsLink ? vehicle.satrackGpsLink : null;
      this.onChangeSatrackGpsLink.emit({ vehicleId, gpsLink });
    };
    if (this.utils.isTeclogiUserOrClient) {
      this.unsubscribeSatrackGpsLinkChanges = FirebaseApi.firestore.collection('Vehicle').doc(vehicleId).onSnapshot(onSnapshot);
      return;
    }
    await DynamicFirebase.configFirebaseByCompanyId('default');
    // The watch was stopped or replaced while Firebase was being configured
    if (watchId !== this.satrackGpsLinkWatchId) return;
    const firestore = DynamicFirebase.getFirestoreByCompanyId('default');
    this.unsubscribeSatrackGpsLinkChanges = firestore.collection('Vehicle').doc(vehicleId).onSnapshot(onSnapshot);
  }

  /**
  * @param {string} vehicleId stops the watch only when it's this vehicle's, so a detail being destroyed doesn't stop
  * the one another detail opened; without it, any watch is stopped
  */
  public stopWatchSatrackGpsLink(vehicleId?: string) {
    if (vehicleId && vehicleId !== this.satrackGpsLinkWatchedVehicleId) return;
    this.satrackGpsLinkWatchedVehicleId = null;
    this.satrackGpsLinkWatchId++;
    if (this.unsubscribeSatrackGpsLinkChanges) {
      this.unsubscribeSatrackGpsLinkChanges();
      this.unsubscribeSatrackGpsLinkChanges = null;
    }
  }

  public async watchUser(userId: string) {
    this.unsubscribeUserChanges = (await this.getUserCollection(userId)).onSnapshot((snapshot) => {
      const user = snapshot.data() as User;
      if (!user || !user.information || !user.information.document) return;
      const userSanitized = this.sanitizeUser(user);
      this.onChangeUser.emit(userSanitized);
    });
  }

  public sanitizeUser(user: User): User {
    if (!user) return null;
    Object.keys(user).forEach(key => {
      if (user[key] === null || user[key] === undefined)
        delete user[key];
      else if (typeof user[key] === 'object') {
        if (this.utils.isDefined(user[key].seconds) && this.utils.isDefined(user[key].nanoseconds)) {
          const date = new Date(user[key].seconds * 1000 + user[key].nanoseconds / 1000000);
          if (this.userFieldsTZ.includes(key))
            user[key] = DateManager.dateToString(date, 'YYYY-MM-DDTHH:MM:SSZ');
          else if (this.userFieldsYYYYMMDD.includes(key))
            user[key] = DateManager.dateToString(date, 'YYYY-MM-DD');
          else if (this.userFieldsDDMMYYYY.includes(key))
            user[key] = DateManager.dateToString(date, 'DD/MM/YYYY');
          else if (this.userFieldsUTC0.includes(key))
            user[key] = DateManager.utcTransform(date);
          else
            user[key] = DateManager.dateToString(date);
        }
        user[key] = this.sanitizeUser(user[key]);
        if (Object.keys(user[key]).length === 0)
          delete user[key];
      }
    });
    return user as User;
  }

  public stopWatchUser() {
    if (this.unsubscribeUserChanges) {
      this.unsubscribeUserChanges();
      this.unsubscribeUserChanges = null;
    }
  }

  public async watchOwner(ownerId: string) {
    this.unsubscribeOwnerChanges = (await this.getUserCollection(ownerId)).onSnapshot((snapshot) => {
      const owner = snapshot.data() as User;
      if (!owner || !owner.information || !owner.information.document) return;
      const ownerSanitized = this.sanitizeUser(owner);
      this.onChangeOwner.emit(ownerSanitized);
    });
  }

  public stopWatchOwner() {
    if (this.unsubscribeOwnerChanges) {
      this.unsubscribeOwnerChanges();
      this.unsubscribeOwnerChanges = null;
    }
  }

  public async watchAdministrator(administratorId: string) {
    this.unsubscribeAdministratorChanges = (await this.getUserCollection(administratorId)).onSnapshot((snapshot) => {
      const admin = snapshot.data() as User;
      if (!admin || !admin.information || !admin.information.document) return;
      const adminSanitized = this.sanitizeUser(admin);
      this.onChangeAdministrator.emit(adminSanitized);
    });
  }

  public stopWatchAdministrator() {
    if (this.unsubscribeAdministratorChanges) {
      this.unsubscribeAdministratorChanges();
      this.unsubscribeAdministratorChanges = null;
    }
  }

  public async getUserCollection(id: string) {
    await DynamicFirebase.configFirebaseByCompanyId('default');
    const firestore = DynamicFirebase.getFirestoreByCompanyId('default');
    return firestore.collection('User').doc(id);
  }

  public getOptionsFilter(): FilterModel[] {
    return [
      {
        filterName: Filter.showFilterVehicleLicensePlate,
        validations: true,
        controls: { id: new FormControl('') },
        type: 'input',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleCompany,
        validations: true,
        controls: { companyId: new FormControl('') },
        type: 'other',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleAccountState,
        validations: true,
        controls: { stateAccountAdvance: new FormControl(''), stateAccountBalance: new FormControl('') },
        type: 'select',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleState,
        validations: true,
        controls: { state: new FormControl('') },
        type: 'select',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleType,
        validations: true,
        controls: { type: new FormControl('') },
        type: 'select',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleOwner,
        validations: true,
        controls: { owner: new FormControl('') },
        type: 'input',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleAdministrator,
        validations: true,
        controls: { administrator: new FormControl('') },
        type: 'input',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleDriver,
        validations: true,
        controls: { driver: new FormControl('') },
        type: 'input',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleTrailerId,
        validations: true,
        controls: { trailerId: new FormControl('') },
        type: 'input',
        typeFilter: 'dynamic'
      },
      {
        filterName: Filter.showFilterVehicleHolderCompany,
        validations: true,
        controls: { holderCompany: this.getUserCompanyId() },
        type: 'input',
        typeFilter: 'static'
      }
    ];
  }
}
